import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, X, Calendar, FileSpreadsheet, Settings, Users, ShieldAlert, ArrowLeftRight, FileText, Download } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import LeaveReportView from './LeaveReportView';
import HolidayCalendarManagement from './HolidayCalendarManagement';
import MarkAbsenteeManagement from './MarkAbsenteeManagement';
import SearchableSelect from '../common/SearchableSelect';
import ScrollableTableContainer from '../common/ScrollableTableContainer';
import { formatDate } from '../../utils/dateUtils';

const inputClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-800',
  maker_approved: 'bg-sky-100 text-sky-800',
  checker_approved: 'bg-blue-100 text-blue-900',
  hq_approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-200 text-gray-700',
};

const formatStatus = (status) => (status || '').replace(/_/g, ' ').toUpperCase();

const LeaveManagement = ({ workflowMode }) => {
  const { user, hasPermission } = useAuth();
  const isAdmin = user?.role_name === 'super_administrator' || (hasPermission && hasPermission('*'));
  const mode = workflowMode || 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const subtabFromUrl = searchParams.get('subtab');
  const [activeTab, setActiveTabState] = useState(() => {
    return subtabFromUrl || localStorage.getItem('active_leave_subtab') || 'requests';
  });

  useEffect(() => {
    const subtab = localStorage.getItem('active_leave_subtab');
    if (subtab) {
      setActiveTab(subtab);
      localStorage.removeItem('active_leave_subtab');
    }
  }, []);

  useEffect(() => {
    if (subtabFromUrl && subtabFromUrl !== activeTab) {
      setActiveTabState(subtabFromUrl);
    }
  }, [subtabFromUrl, activeTab]);

  const setActiveTab = (tabId, replace = false) => {
    setActiveTabState(tabId);
    const nextParams = new URLSearchParams(searchParams);
    if (!tabId || tabId === 'requests') {
      nextParams.delete('subtab');
    } else {
      nextParams.set('subtab', tabId);
    }
    setSearchParams(nextParams, { replace });
  };

  // Common State
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [error, setErrorState] = useState('');
  const setError = (msg) => {
    setErrorState(msg || '');
    if (msg) showError(msg);
  };
  const setSuccessMsg = (msg) => { if (msg) showSuccess(msg); };

  // -----------------------------
  // TAB 1: LEAVE REQUESTS
  // -----------------------------
  const [applications, setApplications] = useState([]);
  const [showAdminCreateModal, setShowAdminCreateModal] = useState(false);
  const [adminLeaveForm, setAdminLeaveForm] = useState({ employee_id: '', leave_type_id: '', from_date: '', to_date: '', reason: '', medical_certificate: false });
  const [adminLeaveTypes, setAdminLeaveTypes] = useState([]);
  const [calculatedDays, setCalculatedDays] = useState(0);

  useEffect(() => {
    if (adminLeaveForm.from_date && adminLeaveForm.to_date) {
      const start = new Date(adminLeaveForm.from_date);
      const end = new Date(adminLeaveForm.to_date);
      if (end >= start) {
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        setCalculatedDays(diffDays);
      } else {
        setCalculatedDays(0);
      }
    } else {
      setCalculatedDays(0);
    }
  }, [adminLeaveForm.from_date, adminLeaveForm.to_date]);

  const fetchLeaveTypes = async () => {
    try {
      const res = await axios.get('/v1/leaves/types');
      setAdminLeaveTypes(res.data || []);
    } catch (e) {
      console.error('Failed to fetch leave types:', e);
    }
  };

  const handleCreateLeaveRequest = async (e) => {
    e.preventDefault();
    if (!adminLeaveForm.employee_id || !adminLeaveForm.leave_type_id || !adminLeaveForm.from_date || !adminLeaveForm.to_date || !adminLeaveForm.reason) {
      setError("Please fill out all required fields.");
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const payload = {
        employee_id: parseInt(adminLeaveForm.employee_id),
        leave_type_id: parseInt(adminLeaveForm.leave_type_id),
        from_date: adminLeaveForm.from_date,
        to_date: adminLeaveForm.to_date,
        reason: adminLeaveForm.reason,
        medical_certificate: adminLeaveForm.medical_certificate
      };
      await axios.post('/v1/leaves/applications/admin', payload);
      setSuccessMsg("Leave application created successfully.");
      setShowAdminCreateModal(false);
      setAdminLeaveForm({ employee_id: '', leave_type_id: adminLeaveTypes[0]?.id || '', from_date: '', to_date: '', reason: '', medical_certificate: false });
      fetchApplications();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Failed to create leave request.");
    } finally {
      setLoading(false);
    }
  };
  const [employees, setEmployees] = useState([]);

  const employeeOptions = React.useMemo(() => {
    return employees
      .filter((emp) => {
        if (emp.emp_code && emp.emp_code.toLowerCase().startsWith('admin')) {
          return false;
        }
        if (emp.status === 'deleted') {
          return false;
        }
        if (emp.status !== 'active') {
          return false;
        }
        return true;
      })
      .map((emp) => ({
        value: emp.id,
        label: `${emp.emp_code} - ${emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim()}`,
      }));
  }, [employees]);

  const [employeePolicy, setEmployeePolicy] = useState(null);
  const [selectedEmployeeBalances, setSelectedEmployeeBalances] = useState([]);
  const [policyCheckLoading, setPolicyCheckLoading] = useState(false);
  const [policyError, setPolicyError] = useState(null);

  const [deductionPreview, setDeductionPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Fetch deduction preview
  useEffect(() => {
    const fetchDeductionPreview = async () => {
      const { employee_id, leave_type_id, from_date, to_date } = adminLeaveForm;
      if (!employee_id || !leave_type_id || !from_date || !to_date) {
        setDeductionPreview(null);
        setPreviewError(null);
        return;
      }

      setPreviewLoading(true);
      setPreviewError(null);

      try {
        const res = await axios.get(
          `/v1/leaves/preview-deduction?employee_id=${employee_id}&leave_type_id=${leave_type_id}&from_date=${from_date}&to_date=${to_date}`
        );
        setDeductionPreview(res.data);
      } catch (e) {
        console.error('Failed to fetch deduction preview:', e);
        setPreviewError(e.response?.data?.detail || 'Failed to calculate leave preview.');
        setDeductionPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchDeductionPreview();
  }, [adminLeaveForm.employee_id, adminLeaveForm.leave_type_id, adminLeaveForm.from_date, adminLeaveForm.to_date]);

  const allowedLeaveTypes = React.useMemo(() => {
    return adminLeaveTypes.filter(lt => {
      return selectedEmployeeBalances.some(b => b.leave_type_code === lt.code);
    });
  }, [adminLeaveTypes, selectedEmployeeBalances]);

  const selectedLeaveType = React.useMemo(() => {
    return adminLeaveTypes.find(lt => String(lt.id) === String(adminLeaveForm.leave_type_id));
  }, [adminLeaveTypes, adminLeaveForm.leave_type_id]);

  const selectedBalance = React.useMemo(() => {
    return selectedLeaveType
      ? selectedEmployeeBalances.find(b => b.leave_type_code === selectedLeaveType.code)
      : null;
  }, [selectedLeaveType, selectedEmployeeBalances]);

  // Reset leave type when employee changes to prevent retaining from previous employee
  useEffect(() => {
    setAdminLeaveForm(prev => ({ ...prev, leave_type_id: '' }));
  }, [adminLeaveForm.employee_id]);

  // Fetch policy and balances when employee or selected date year changes
  useEffect(() => {
    const checkEmployeePolicyAndBalances = async () => {
      if (!adminLeaveForm.employee_id) {
        setEmployeePolicy(null);
        setSelectedEmployeeBalances([]);
        setPolicyError(null);
        return;
      }

      setPolicyCheckLoading(true);
      setPolicyError(null);

      try {
        // 1. Fetch employee assignments to check for explicit mapping
        const assignmentsRes = await axios.get(`/v1/leave-policy-assignments?employee_id=${adminLeaveForm.employee_id}`);
        const activeAssignment = (assignmentsRes.data || []).find(m => m.status === 'active');

        if (!activeAssignment) {
          setEmployeePolicy(null);
          setSelectedEmployeeBalances([]);
          setPolicyError('NO_POLICY');
          return;
        }

        // 2. Fetch balances for the correct year based on from_date or current year
        const year = adminLeaveForm.from_date
          ? new Date(adminLeaveForm.from_date).getFullYear()
          : new Date().getFullYear();

        const balancesRes = await axios.get(`/v1/leave-balances/employee/${adminLeaveForm.employee_id}?year=${year}`);

        setEmployeePolicy(balancesRes.data.policy || null);
        const balances = balancesRes.data.balances || [];
        setSelectedEmployeeBalances(balances);

        if (balances.length === 0) {
          setPolicyError('NO_LEAVE_TYPES');
        }
      } catch (e) {
        console.error('Failed to fetch employee policy or balances:', e);
        setPolicyError('NO_POLICY');
      } finally {
        setPolicyCheckLoading(false);
      }
    };

    checkEmployeePolicyAndBalances();
  }, [adminLeaveForm.employee_id, adminLeaveForm.from_date]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appActionModal, setAppActionModal] = useState({ open: false, type: '', app: null, remarks: '' });

  // -----------------------------
  // TAB 2: POLICIES CONFIG
  // -----------------------------
  const [policies, setPolicies] = useState([]);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingPolicyId, setEditingPolicyId] = useState(null);
  const [policyForm, setPolicyForm] = useState({
    code: '',
    name: '',
    description: '',
    status: 'active',
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: '',
    assign_all: false,
    assigned_employee_ids: [],
    max_accumulated_leave_balance: 300,
    allocation_rules: [],
    consumption_rules: []
  });

  // -----------------------------
  // TAB 3: ASSIGNMENTS
  // -----------------------------
  const [assignments, setAssignments] = useState([]);
  const [assignForm, setAssignForm] = useState({ policy_id: '', mode: 'all', selected_emp_ids: [], effective_from: new Date().toISOString().split('T')[0] });
  const [empSearch, setEmpSearch] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState({ search: '', policy_id: '', status: 'active' });

  // -----------------------------
  // TAB 4: WORK CALENDAR
  // -----------------------------
  const [calMonth, setCalMonth] = useState(new Date().getMonth() + 1);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [currentCalendar, setCurrentCalendar] = useState(null);
  const [editingDay, setEditingDay] = useState(null);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);

  // -----------------------------
  // TAB 5: ABSENCE REGULARISATION
  // -----------------------------
  const [regMonth, setRegMonth] = useState(new Date().getMonth() + 1);
  const [regYear, setRegYear] = useState(new Date().getFullYear());
  const [regEmpId, setRegEmpId] = useState('');
  const [absenceData, setAbsenceData] = useState(null);
  const [selectedAbsenceDates, setSelectedAbsenceDates] = useState([]);
  const [regLeaveType, setRegLeaveType] = useState('');
  const [regReason, setRegReason] = useState('');
  const [employeeBalances, setEmployeeBalances] = useState(null);
  const [regularisationResult, setRegularisationResult] = useState(null);

  // Auto-refresh absence data when month/year/employee changes
  useEffect(() => {
    if (regEmpId) {
      handleCheckAbsence();
    }
  }, [regMonth, regYear, regEmpId]);

  // -----------------------------
  // TAB 6: LEAVE ARREARS
  // -----------------------------
  const [arrears, setArrears] = useState([]);
  const [arrearSearch, setArrearSearch] = useState('');
  const [arrearStatusFilter, setArrearStatusFilter] = useState('ALL');
  const [arrearMonthFilter, setArrearMonthFilter] = useState('ALL');
  const [arrearYearFilter, setArrearYearFilter] = useState('ALL');
  const [selectedArrearDetail, setSelectedArrearDetail] = useState(null);

  // Group arrears by employee, payroll month, and payroll year
  const getGroupedArrears = () => {
    const grouped = {};

    arrears.forEach(arrear => {
      const key = `${arrear.employee_id}_${arrear.original_payroll_month || 'N/A'}_${arrear.original_payroll_year || 'N/A'}`;

      if (!grouped[key]) {
        grouped[key] = {
          employee_id: arrear.employee_id,
          employee_name: arrear.employee_name,
          employee_code: arrear.employee_code,
          designation: arrear.designation,
          payroll_month: arrear.original_payroll_month,
          payroll_year: arrear.original_payroll_year,
          total_days: 0,
          total_amount: 0,
          records: [],
          all_statuses: []
        };
      }

      grouped[key].total_days += parseFloat(arrear.days) || 0;
      grouped[key].total_amount += parseFloat(arrear.amount) || 0;
      grouped[key].records.push(arrear);
      grouped[key].all_statuses.push(arrear.status);
    });

    return Object.values(grouped).map(group => {
      // Determine monthly status
      const hasPending = group.all_statuses.includes('PENDING');
      const hasApplied = group.all_statuses.includes('APPLIED');

      let monthlyStatus = 'PENDING';
      if (hasApplied && hasPending) {
        monthlyStatus = 'PARTIALLY_PAID';
      } else if (hasApplied) {
        monthlyStatus = 'PAID';
      }

      return {
        ...group,
        monthly_status: monthlyStatus
      };
    });
  };

  const groupedArrears = getGroupedArrears();

  // -----------------------------
  // TAB 7: LEAVE SHEET
  // -----------------------------
  const [sheetEmpId, setSheetEmpId] = useState('');
  const [sheetYear, setSheetYear] = useState(new Date().getFullYear());
  const [sheetData, setSheetData] = useState(null);
  const [sheetRefreshKey, setSheetRefreshKey] = useState(0);

  // ----------------------------------------------------
  // FETCHERS
  // ----------------------------------------------------
  const fetchEmployees = async () => {
    try {
      const res = await axios.get('/v1/benfish-employees/search?limit=500');
      setEmployees(res.data.employees || []);
    } catch (e) {
      console.error('Failed to fetch employees:', e);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/v1/leaves/applications?size=200');
      setApplications(Array.isArray(res.data) ? res.data : (res.data.items || []));
    } catch (e) {
      console.error(e);
      if (e.response && e.response.status === 403) {
        setError("You do not have permission to view leave applications.");
      } else {
        setError('Failed to fetch leave applications');
      }
    } finally { setLoading(false); }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setError('');
    setSuccessMsg('');
  };

  const fetchPolicies = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/v1/leave-policies');
      setPolicies(res.data.policies || []);
    } catch (e) {
      console.error(e);
      if (e.response && e.response.status === 403) {
        setError("You do not have permission to manage leave policies.");
      } else {
        setError("Unable to load leave policies.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditPolicy = (pol) => {
    setEditingPolicyId(pol.id);
    setPolicyForm({
      code: pol.code || '',
      name: pol.name || '',
      description: pol.description || '',
      status: pol.status || 'active',
      effective_from: pol.effective_from || new Date().toISOString().split('T')[0],
      effective_to: pol.effective_to || '',
      assign_all: pol.assigned_employee_ids && pol.assigned_employee_ids.length > 0 ? false : (pol.assign_all || false),
      assigned_employee_ids: pol.assigned_employee_ids || [],
      max_accumulated_leave_balance: pol.max_accumulated_leave_balance || 300,
      allocation_rules: pol.allocation_rules ? pol.allocation_rules.map(r => ({
        leave_type: r.leave_type || 'EL',
        allocation_period: r.allocation_period || 'ANNUAL',
        allocation_days: r.allocation_days || 0,
        is_applicable: r.is_applicable !== undefined ? r.is_applicable : true,
        is_paid: r.is_paid !== undefined ? r.is_paid : true
      })) : [],
      consumption_rules: (pol.consumption_rules || pol.conversion_rules || []).map(c => ({
        id: c.id,
        source_leave_type: c.source_leave_type || '',
        balance_leave_type: c.balance_leave_type || c.fallback_leave_type || '',
        payroll_treatment: c.payroll_treatment || c.result_leave_type || 'NORMAL',
        request_unit: c.request_unit !== undefined ? c.request_unit : (c.source_units || 1.0),
        balance_unit: c.balance_unit !== undefined ? c.balance_unit : (c.fallback_units || 1.0),
        priority: c.priority || 1,
        continue_if_insufficient: c.continue_if_insufficient !== undefined ? c.continue_if_insufficient : true,
        is_enabled: c.is_enabled !== undefined ? c.is_enabled : true,
        migration_status: c.migration_status || null
      }))
    });
    setShowPolicyModal(true);
  };

  const handleCreatePolicyClick = () => {
    setEditingPolicyId(null);
    setPolicyForm({
      code: '',
      name: '',
      description: '',
      status: 'active',
      effective_from: new Date().toISOString().split('T')[0],
      effective_to: '',
      assign_all: false,
      assigned_employee_ids: [],
      max_accumulated_leave_balance: 300,
      allocation_rules: [],
      consumption_rules: []
    });
    setShowPolicyModal(true);
  };

  const handleTogglePolicyStatus = async (pol) => {
    setLoading(true);
    setError('');
    try {
      const newStatus = pol.status === 'active' ? 'disabled' : 'active';
      const updatedRules = pol.allocation_rules ? pol.allocation_rules.map(r => ({
        leave_type: r.leave_type || 'EL',
        allocation_period: r.allocation_period || 'ANNUAL',
        allocation_days: r.allocation_days || 0,
        is_applicable: r.is_applicable !== undefined ? r.is_applicable : true,
        is_paid: r.is_paid !== undefined ? r.is_paid : true
      })) : [];

      const updatedConversions = (pol.consumption_rules || pol.conversion_rules || []).map(c => ({
        source_leave_type: c.source_leave_type,
        balance_leave_type: c.balance_leave_type || c.fallback_leave_type || '',
        payroll_treatment: c.payroll_treatment || c.result_leave_type || 'NORMAL',
        request_unit: c.request_unit !== undefined ? c.request_unit : (c.source_units || 1.0),
        balance_unit: c.balance_unit !== undefined ? c.balance_unit : (c.fallback_units || 1.0),
        priority: c.priority,
        continue_if_insufficient: c.continue_if_insufficient !== undefined ? c.continue_if_insufficient : true,
        is_enabled: c.is_enabled,
        migration_status: c.migration_status || null
      }));

      await axios.put(`/v1/leave-policies/${pol.id}`, {
        code: pol.code,
        name: pol.name,
        description: pol.description,
        status: newStatus,
        effective_from: pol.effective_from,
        effective_to: pol.effective_to,
        max_accumulated_leave_balance: pol.max_accumulated_leave_balance,
        allocation_rules: updatedRules,
        consumption_rules: updatedConversions,
        assigned_employee_ids: pol.assigned_employee_ids || []
      });
      setSuccessMsg(`Policy status updated to ${newStatus}`);
      fetchPolicies();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Failed to update policy status');
    } finally {
      setLoading(false);
    }
  };

  const getUniqueLeaveTypes = (rules) => {
    if (!rules) return [];
    const types = rules.map(r => r.leave_type);
    return [...new Set(types)];
  };

  const handleEntitlementDayChange = (leaveType, period, val) => {
    const numVal = parseFloat(val) || 0;
    const rules = [...policyForm.allocation_rules];
    let ruleIdx = rules.findIndex(r => r.leave_type === leaveType && r.allocation_period === period);

    if (ruleIdx >= 0) {
      rules[ruleIdx] = { ...rules[ruleIdx], allocation_days: numVal, is_applicable: numVal > 0 };
    } else {
      rules.push({
        leave_type: leaveType,
        allocation_period: period,
        allocation_days: numVal,
        is_applicable: numVal > 0,
        is_paid: true
      });
    }
    setPolicyForm({ ...policyForm, allocation_rules: rules });
  };

  const handleEntitlementFlagChange = (leaveType, field, value) => {
    const rules = policyForm.allocation_rules.map(r => {
      if (r.leave_type === leaveType) {
        return { ...r, [field]: value };
      }
      return r;
    });
    setPolicyForm({ ...policyForm, allocation_rules: rules });
  };

  const handleEntitlementAllocationTypeChange = (leaveType, allocationType) => {
    const rules = policyForm.allocation_rules.filter(r => r.leave_type !== leaveType);

    if (allocationType === 'HALF_YEARLY') {
      rules.push({ leave_type: leaveType, allocation_period: 'JAN_JUN', allocation_days: 0, is_applicable: false, is_paid: true });
      rules.push({ leave_type: leaveType, allocation_period: 'JUL_DEC', allocation_days: 0, is_applicable: false, is_paid: true });
    } else {
      rules.push({ leave_type: leaveType, allocation_period: 'ANNUAL', allocation_days: 0, is_applicable: false, is_paid: true });
    }

    setPolicyForm({ ...policyForm, allocation_rules: rules });
  };

  const getAllocationTypeForLeaveType = (leaveType) => {
    const rules = policyForm.allocation_rules.filter(r => r.leave_type === leaveType);
    if (rules.some(r => r.allocation_period === 'ANNUAL')) return 'ANNUAL';
    if (rules.some(r => r.allocation_period === 'JAN_JUN') || rules.some(r => r.allocation_period === 'JUL_DEC')) return 'HALF_YEARLY';
    return 'HALF_YEARLY';
  };

  const handleRemoveEntitlementType = (leaveType) => {
    const rules = policyForm.allocation_rules.filter(r => r.leave_type !== leaveType);
    const conversions = policyForm.consumption_rules.filter(c => c.source_leave_type !== leaveType && c.balance_leave_type !== leaveType);
    setPolicyForm({ ...policyForm, allocation_rules: rules, consumption_rules: conversions });
  };

  const [showLeaveTypeSelector, setShowLeaveTypeSelector] = useState(false);

  const handleAddEntitlementRow = () => {
    setShowLeaveTypeSelector(true);
  };

  const handleSelectLeaveType = (leaveType) => {
    const exists = policyForm.allocation_rules.some(r => r.leave_type === leaveType);
    if (exists) {
      setError(`Leave type ${leaveType} is already in entitlements.`);
      return;
    }

    const newRules = [];
    if (leaveType === 'CL') {
      newRules.push({
        leave_type: leaveType,
        allocation_period: 'ANNUAL',
        allocation_days: 8,
        is_applicable: true,
        is_paid: true
      });
    } else {
      newRules.push({
        leave_type: leaveType,
        allocation_period: 'JAN_JUN',
        allocation_days: 0,
        is_applicable: false,
        is_paid: true
      });
      newRules.push({
        leave_type: leaveType,
        allocation_period: 'JUL_DEC',
        allocation_days: 0,
        is_applicable: false,
        is_paid: true
      });
    }

    setPolicyForm({
      ...policyForm,
      allocation_rules: [...policyForm.allocation_rules, ...newRules]
    });
    setShowLeaveTypeSelector(false);
  };

  const handleAddConsumptionRow = () => {
    const uniqueEntitlements = getUniqueLeaveTypes(policyForm.allocation_rules);
    const filteredEntitlements = uniqueEntitlements.filter(t => t !== 'HPL' && t !== 'FPL');
    const source = filteredEntitlements[0] || '';
    const balance = filteredEntitlements[1] || '';

    setPolicyForm({
      ...policyForm,
      consumption_rules: [
        ...(policyForm.consumption_rules ?? []),
        {
          source_leave_type: source,
          balance_leave_type: balance,
          payroll_treatment: 'NORMAL',
          request_unit: 1.0,
          balance_unit: 1.0,
          priority: (policyForm.consumption_rules ?? []).length + 1,
          continue_if_insufficient: true,
          is_enabled: true,
          migration_status: null
        }
      ]
    });
  };

  const handleRemoveConsumptionRow = (idx) => {
    const conversions = [...(policyForm.consumption_rules ?? [])];
    conversions.splice(idx, 1);
    const updated = conversions.map((c, i) => ({ ...c, priority: i + 1 }));
    setPolicyForm({ ...policyForm, consumption_rules: updated });
  };

  const handleConsumptionRuleChange = (idx, field, value) => {
    const conversions = [...(policyForm.consumption_rules ?? [])];
    conversions[idx] = { ...conversions[idx], [field]: value };
    setPolicyForm({ ...policyForm, consumption_rules: conversions });
  };

  const fetchAssignments = async () => {
    try {
      const res = await axios.get('/v1/leave-policy-assignments');
      setAssignments(res.data || []);
    } catch (e) {
      console.error(e);
      if (e.response && e.response.status === 403) {
        setError("You do not have permission to view policy assignments.");
      }
    }
  };

  const fetchWorkCalendar = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`/v1/work-calendars?month=${calMonth}&year=${calYear}`);
      const list = res.data || [];
      if (list.length > 0) {
        const detailRes = await axios.get(`/v1/work-calendars/${list[0].id}`);
        setCurrentCalendar(detailRes.data);
      } else {
        setCurrentCalendar(null);
      }
    } catch (e) {
      console.error(e);
      if (e.response && e.response.status === 403) {
        setError("You do not have permission to view work calendars.");
      } else {
        setError("Failed to fetch work calendar. Please try again.");
      }
      setCurrentCalendar(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchArrears = async () => {
    try {
      const res = await axios.get('/v1/leave-arrears');
      setArrears(res.data || []);
    } catch (e) {
      console.error(e);
      if (e.response && e.response.status === 403) {
        setError("You do not have permission to view leave arrears.");
      }
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchPolicies();
    fetchLeaveTypes();
  }, []);

  // Auto-fetch calendar when month/year changes
  useEffect(() => {
    if (activeTab === 'calendar') {
      fetchWorkCalendar();
    }
  }, [calMonth, calYear, activeTab]);

  useEffect(() => {
    if (activeTab === 'requests') fetchApplications();
    if (activeTab === 'policies') fetchPolicies();
    if (activeTab === 'assignments') fetchAssignments();
    if (activeTab === 'calendar') fetchWorkCalendar();
    if (activeTab === 'arrears') fetchArrears();
  }, [activeTab]);

  // ----------------------------------------------------
  // WORKFLOW & APPROVAL ACTIONS
  // ----------------------------------------------------
  const handleAppAction = async () => {
    if (!appActionModal.app) return;
    const { type, app, remarks } = appActionModal;
    setError(null);
    if (type === 'reject') {
      const stripped = (remarks || '').trim();
      if (!stripped) {
        setError('Rejection reason is required.');
        return;
      }
      if (stripped.length < 10) {
        setError('Rejection reason must be at least 10 characters long.');
        return;
      }
    }
    setLoading(true);
    try {
      let endpoint = '';
      if (type === 'maker') endpoint = `/v1/leaves/applications/${app.id}/maker-approve`;
      else if (type === 'checker') endpoint = `/v1/leaves/applications/${app.id}/approve`;
      else if (type === 'hq') endpoint = `/v1/leaves/applications/${app.id}/hq-approve`;
      else if (type === 'reject') endpoint = `/v1/leaves/applications/${app.id}/reject`;

      const payload = type === 'reject'
        ? { rejection_reason: remarks }
        : { remarks: remarks || 'Approved via Leave Management UI' };

      const res = await axios.post(endpoint, payload);
      setSuccessMsg(`Leave request ${app.application_number} updated successfully!`);
      setAppActionModal({ open: false, type: '', app: null, remarks: '' });
      fetchApplications();
    } catch (e) {
      const msg = e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Error processing leave action';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------
  // CALENDAR ACTIONS
  // ----------------------------------------------------
  const handleGenerateCalendar = async () => {
    setLoading(true);
    try {
      const res = await axios.post('/v1/work-calendars/generate', { month: calMonth, year: calYear });
      setSuccessMsg('Work calendar draft generated successfully');
      fetchWorkCalendar();
    } catch (e) { setError('Failed to generate calendar'); }
    finally { setLoading(false); }
  };

  const handleFinalizeCalendar = async () => {
    if (!currentCalendar) return;
    setLoading(true);
    try {
      const res = await axios.post(`/v1/work-calendars/${currentCalendar.id}/finalize`);
      setSuccessMsg('Work calendar finalized successfully!');
      setShowFinalizeModal(false);
      fetchWorkCalendar();
    } catch (e) { setError('Failed to finalize calendar'); }
    finally { setLoading(false); }
  };

  const handleUpdateCalendarDay = async () => {
    if (!editingDay || !currentCalendar) return;

    // Validation: holiday_name required for HOLIDAY day type
    if (editingDay.day_type === 'HOLIDAY' && !editingDay.holiday_name?.trim()) {
      setError('Holiday name is required when day type is HOLIDAY');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        day_type: editingDay.day_type,
        holiday_name: editingDay.holiday_name || null,
        remarks: editingDay.remarks || null
      };

      // If editingDay has an id, it's an existing day - update it
      if (editingDay.id) {
        await axios.put(`/v1/work-calendars/${currentCalendar.id}/days/${editingDay.id}`, payload);
      } else {
        // For new days (shouldn't happen with current API, but kept for safety)
        setError('Cannot create new days. Please edit existing days only.');
        setLoading(false);
        return;
      }

      setSuccessMsg('Calendar day updated successfully');
      setEditingDay(null);
      fetchWorkCalendar();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to update calendar day');
    }
    finally { setLoading(false); }
  };

  // ----------------------------------------------------
  // ABSENCE REGULARISATION ACTIONS
  // ----------------------------------------------------
  const handleCheckAbsence = useCallback(async () => {
    if (!regEmpId) {
      setError('Please select an employee');
      return;
    }
    setLoading(true);
    setError('');
    setAbsenceData(null);
    setSelectedAbsenceDates([]);
    setEmployeeBalances(null);
    setRegularisationResult(null);
    try {
      // Use Mark Absentee endpoint as source of truth
      const res = await axios.get(`/v1/mark-absentee/employee/${regEmpId}`, {
        params: {
          payroll_month: regMonth,
          payroll_year: regYear
        }
      });

      // Map Mark Absentee data structure to absenceData
      const calendarGrid = res.data.calendar_grid || [];
      const totalDays = calendarGrid.length;
      const absentDates = calendarGrid.filter(day => day.existing_absent).map(day => day.date);
      const presentDates = calendarGrid.filter(day => !day.existing_absent && !day.is_on_leave).map(day => day.date);
      const leaveDates = calendarGrid.filter(day => day.is_on_leave).map(day => day.date);

      setAbsenceData({
        total_days: totalDays,
        absent_days: absentDates.length,
        present_days: presentDates.length,
        paid_leave_days: leaveDates.length,
        absent_dates: absentDates,
        calendar_grid: calendarGrid,
        attendance_from: res.data.attendance_from,
        attendance_to: res.data.attendance_to
      });

      // Load employee balances for the specific month/year using new date-aware endpoint
      const balanceRes = await axios.get(`/v1/leave-balances/employee/${regEmpId}/for-period?month=${regMonth}&year=${regYear}`);
      setEmployeeBalances(balanceRes.data);
    } catch (e) {
      console.error(e);
      if (e.response?.status === 404) {
        setError('Employee not found');
      } else if (e.response?.status === 400) {
        setError(e.response?.data?.detail || 'No finalized work calendar found for this month');
      } else {
        setError('Failed to check absence');
      }
    } finally {
      setLoading(false);
    }
  }, [regEmpId, regMonth, regYear]);

  const handleToggleDateSelection = (date) => {
    setSelectedAbsenceDates(prev =>
      prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
    );
  };

  const handleSelectAllDates = () => {
    if (absenceData?.absent_dates) {
      setSelectedAbsenceDates(absenceData.absent_dates);
    }
  };

  const handleClearDateSelection = () => {
    setSelectedAbsenceDates([]);
  };

  const handleRegulariseAbsence = async () => {
    if (!regEmpId || selectedAbsenceDates.length === 0 || !regLeaveType || !regReason.trim()) {
      setError('Please select employee, dates, leave type, and provide a reason');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        employee_id: parseInt(regEmpId),
        absence_dates: selectedAbsenceDates,
        requested_leave_type: regLeaveType,
        reason: regReason
      };
      const res = await axios.post('/v1/leave-absence/regularize', payload);
      setRegularisationResult(res.data);
      setSuccessMsg(res.data.message);

      // Refresh absence data and balances
      await handleCheckAbsence();
      fetchArrears();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Failed to regularise absence');
    } finally {
      setLoading(false);
    }
  };
  const handleSavePolicy = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (editingPolicyId) {
        await axios.put(`/v1/leave-policies/${editingPolicyId}`, policyForm);
        setSuccessMsg("Leave policy updated successfully");
      } else {
        await axios.post('/v1/leave-policies', policyForm);
        setSuccessMsg("Leave policy created successfully");
      }
      setShowPolicyModal(false);
      setEditingPolicyId(null);
      fetchPolicies();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Error saving policy');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPolicy = async (e) => {
    e.preventDefault();
    if (!assignForm.policy_id) { setError('Please select a policy'); return; }
    setLoading(true);
    setError('');
    try {
      const payload = {
        policy_id: parseInt(assignForm.policy_id),
        assign_all: assignForm.mode === 'all',
        employee_ids: assignForm.mode === 'selected' ? assignForm.selected_emp_ids : [],
        effective_from: assignForm.effective_from
      };

      const res = await axios.post(`/v1/leave-policies/${assignForm.policy_id}/assign`, payload);
      setSuccessMsg(res.data.message || `Policy assigned successfully to ${res.data.details?.assigned_count || 0} employee(s)`);
      setAssignForm({ ...assignForm, selected_emp_ids: [] });
      fetchAssignments();
      fetchPolicies();
    } catch (e) {
      console.error('Assignment error:', e);
      setError(e.response?.data?.detail || 'Error assigning policy');
    }
    finally { setLoading(false); }
  };

  const handleFetchSheet = async () => {
    if (!sheetEmpId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/v1/leave-sheet/employee/${sheetEmpId}?year=${sheetYear}`);
      setSheetData({ ...res.data, year: sheetYear });
      setSheetRefreshKey(prev => prev + 1); // Increment refresh key to force LeaveReportView to refetch
    } catch (e) { setError('Error fetching leave sheet'); }
    finally { setLoading(false); }
  };

  const handleDownloadSheetExcel = () => {
    if (!sheetEmpId) return;
    window.open(`/v1/leave-sheet/employee/${sheetEmpId}/excel?year=${sheetYear}`, '_blank');
  };

  const filteredEmployees = employees.filter(e => {
    const term = empSearch.toLowerCase();
    const name = (e.full_name || `${e.first_name || ''} ${e.last_name || ''}`).toLowerCase();
    const code = (e.emp_code || '').toLowerCase();
    return name.includes(term) || code.includes(term);
  });

  const filteredAssignments = assignments.filter(a => {
    const search = assignmentFilter.search.toLowerCase();
    const name = (a.employee_name || '').toLowerCase();
    const code = (a.employee_code || '').toLowerCase();
    const matchesSearch = !search || name.includes(search) || code.includes(search);
    const matchesPolicy = !assignmentFilter.policy_id || a.policy_id === parseInt(assignmentFilter.policy_id);
    const matchesStatus = !assignmentFilter.status || a.status === assignmentFilter.status;
    return matchesSearch && matchesPolicy && matchesStatus;
  });

  return (
    <div className="space-y-6 p-6 bg-gray-50/50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Policy & Calendar Governance</h1>
          <p className="text-sm text-gray-500">Centralized Leave Approvals, Database-Driven Policy Rules, Monthly Work Calendar & Pension Reports</p>
        </div>
      </div>

      {/* Tabs Bar */}
      <div className="flex space-x-2 border-b border-gray-200 overflow-x-auto pb-1 bg-white p-2 rounded-xl shadow-sm">
        {[
          { id: 'requests', label: 'Leave Requests', icon: FileText, requiredPermission: ['leave:request:view'] },
          { id: 'policies', label: 'Leave Policy Config', icon: Settings, requiredPermission: ['leave:policy:create', 'leave:policy:edit'] },
          { id: 'assignments', label: 'Leave Policy Assignments', icon: Users, requiredPermission: ['leave:policy:assign'] },
          //{ id: 'calendar', label: 'Work Calendar', icon: Calendar, requiredPermission: ['leave:policy:create'] },
          { id: 'mark-absentee', label: 'Mark Absentee', icon: ShieldAlert, requiredPermission: ['leave:absence:summary', 'leave:absence:check'] },
          { id: 'absence', label: 'Absence & Regularisation', icon: ShieldAlert, requiredPermission: ['leave:absence:check', 'leave:absence:regularize'] },
          { id: 'arrears', label: 'Leave Arrears', icon: ArrowLeftRight, requiredPermission: ['leave:arrear:view'] },
          { id: 'sheet', label: 'Leave Sheet', icon: FileSpreadsheet, requiredPermission: ['leave:sheet:generate'] },
        ].filter(tab => isAdmin || (tab.requiredPermission && tab.requiredPermission.some(perm => hasPermission(perm)))).map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${isActive
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* TAB 1: LEAVE REQUESTS */}
      {activeTab === 'requests' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Leave Applications & Approval Workflow</h2>
            <div className="flex items-center gap-3">
              {hasPermission('leave:request:create') && (
                <button
                  onClick={() => {
                    setAdminLeaveForm({
                      employee_id: '',
                      leave_type_id: adminLeaveTypes[0]?.id || '',
                      from_date: '',
                      to_date: '',
                      reason: '',
                      medical_certificate: false
                    });
                    setShowAdminCreateModal(true);
                  }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-xs font-bold transition shadow-sm"
                >
                  <Plus className="w-4 h-4" /> Create Leave Request
                </button>
              )}
              <button onClick={fetchApplications} className="text-xs text-blue-600 font-bold hover:underline">Refresh Applications</button>
            </div>
          </div>

          {loading ? <p className="text-sm text-gray-500 py-4">Loading leave applications from backend...</p> : (
            <ScrollableTableContainer
              header={null}
              maxHeight={500}
              showTopScrollbar={true}
              stickyHeader={true}
            >
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-700 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3">App No</th>
                    <th className="px-4 py-3">Employee</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Dates</th>
                    <th className="px-4 py-3">Days</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {applications.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 font-mono font-medium text-gray-900">{app.application_number}</td>
                      <td className="px-4 py-3 font-medium">{app.employee_name || `Emp #${app.employee_id}`} ({app.employee_code || '-'})</td>
                      <td className="px-4 py-3 font-semibold text-blue-600">{app.leave_type_name || app.leave_type_code || 'Leave'}</td>
                      <td className="px-4 py-3">{app.from_date} to {app.to_date}</td>
                      <td className="px-4 py-3 font-bold">{app.no_of_days}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_BADGE[app.status] || 'bg-gray-100 text-gray-800'}`}>
                          {formatStatus(app.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 space-x-2">
                        {app.status === 'pending' && !isAdmin && hasPermission('leave:request:approve') && (
                          <button
                            onClick={() => setAppActionModal({ open: true, type: 'maker', app, remarks: '' })}
                            className="px-2.5 py-1 bg-indigo-100 text-indigo-800 rounded font-bold text-xs hover:bg-indigo-200"
                          >
                            Forward
                          </button>
                        )}
                        {(app.status === 'maker_approved' || (isAdmin && ['pending', 'maker_approved', 'checker_approved'].includes(app.status))) && hasPermission('leave:request:approve') && (
                          <button
                            onClick={() => setAppActionModal({ open: true, type: 'checker', app, remarks: '' })}
                            className="px-2.5 py-1 bg-green-100 text-green-800 rounded font-bold text-xs hover:bg-green-200"
                          >
                            {isAdmin ? 'Approve' : 'Checker Approve'}
                          </button>
                        )}
                        {app.status === 'checker_approved' && !isAdmin && hasPermission('leave:request:approve') && (
                          <button
                            onClick={() => setAppActionModal({ open: true, type: 'hq', app, remarks: '' })}
                            className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded font-bold text-xs hover:bg-blue-200"
                          >
                            HQ Approve
                          </button>
                        )}
                        {['pending', 'maker_approved', 'checker_approved'].includes(app.status) && hasPermission('leave:request:reject') && (
                          <button
                            onClick={() => setAppActionModal({ open: true, type: 'reject', app, remarks: '' })}
                            className="px-2.5 py-1 bg-red-100 text-red-800 rounded font-bold text-xs hover:bg-red-200"
                          >
                            Reject
                          </button>
                        )}
                        <button onClick={() => setSelectedApp(app)} className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded text-xs font-semibold hover:bg-gray-200">
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                  {applications.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No leave applications found in database.</td></tr>
                  )}
                </tbody>
              </table>
            </ScrollableTableContainer>
          )}
        </div>
      )}

      {/* TAB 2: POLICIES CONFIG */}
      {activeTab === 'policies' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Database-Driven Leave Policies</h2>
              <p className="text-xs text-gray-500">Configure entitlement, carry-forward, and leave conversion rules per policy</p>
            </div>
            <button
              onClick={handleCreatePolicyClick}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 shadow transition"
              style={{ display: hasPermission('leave:policy:create') ? 'flex' : 'none' }}
            >
              <Plus className="w-4 h-4" />
              <span>Create New Leave Policy</span>
            </button>
          </div>

          {loading && policies.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <p className="text-sm text-gray-500 font-medium animate-pulse">Loading leave policies from database...</p>
            </div>
          ) : error && error.includes("permission") ? (
            <div className="text-center py-12 bg-red-50 rounded-xl border border-dashed border-red-200 p-6">
              <p className="text-sm font-semibold text-red-700">You do not have permission to manage leave policies.</p>
              <p className="text-xs text-red-500 mt-1">Please contact your HRMS Administrator for access.</p>
            </div>
          ) : policies.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <p className="text-sm text-gray-500 font-medium">No leave policies have been configured yet.</p>
              <p className="text-xs text-gray-400 mt-1">Click the "Create New Leave Policy" button to add rules.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {policies.map((pol) => (
                <div key={pol.id} className="border border-gray-200 bg-white rounded-xl p-5 space-y-4 hover:shadow-md transition duration-200 flex flex-col justify-between animate-in fade-in zoom-in duration-200">
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg">{pol.name}</h3>
                        <p className="text-xs text-gray-400 font-mono mt-0.5">Code: {pol.code}</p>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${pol.status === 'active' ? 'bg-green-50 text-green-700 border border-green-200' :
                        pol.status === 'draft' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                          'bg-gray-50 text-gray-600 border border-gray-200'
                        }`}>
                        {pol.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{pol.description || 'No description provided'}</p>

                    <div className="border-t border-gray-100 pt-3 space-y-3">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Effective: <span className="font-semibold text-gray-700">{pol.effective_from || 'N/A'}</span></span>
                        <span>Assigned: <span className="font-semibold text-blue-700">{pol.assigned_employee_ids && pol.assigned_employee_ids.length > 0 ? `${pol.assigned_employee_ids.length} Employees` : 'None'}</span></span>
                      </div>

                      <div>
                        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Entitlements</h4>
                        <div className="text-xs text-gray-700 space-y-1">
                          {(() => {
                            const entitlementsByType = {};
                            pol.allocation_rules && pol.allocation_rules.forEach(r => {
                              if (!entitlementsByType[r.leave_type]) {
                                entitlementsByType[r.leave_type] = [];
                              }
                              entitlementsByType[r.leave_type].push(r);
                            });

                            return Object.entries(entitlementsByType).map(([lt, rules]) => {
                              const janJun = rules.find(r => r.allocation_period === 'JAN_JUN');
                              const julDec = rules.find(r => r.allocation_period === 'JUL_DEC');
                              const annual = rules.find(r => r.allocation_period === 'ANNUAL');

                              let display = '';
                              if (annual) {
                                display = `${lt}: ${annual.allocation_days} annually`;
                              } else if (janJun && julDec) {
                                display = `${lt}: ${janJun.allocation_days} Jan-Jun + ${julDec.allocation_days} Jul-Dec`;
                              } else if (janJun) {
                                display = `${lt}: ${janJun.allocation_days} Jan-Jun`;
                              } else if (julDec) {
                                display = `${lt}: ${julDec.allocation_days} Jul-Dec`;
                              }

                              return <div key={lt} className="font-medium">{display}</div>;
                            });
                          })()}
                        </div>
                      </div>

                      {pol.max_accumulated_leave_balance && (
                        <div>
                          <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Leave Policy Limit</h4>
                          <div className="text-xs text-gray-700">Maximum accumulated balance: {pol.max_accumulated_leave_balance} days</div>
                        </div>
                      )}

                      {pol.consumption_rules && pol.consumption_rules.length > 0 && (
                        <div>
                          <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Consumption Rules</h4>
                          <div className="space-y-1 text-xs text-gray-700 font-medium">
                            {pol.consumption_rules.filter(cr => cr.is_enabled).map((cr, i) => (
                              <div key={i}>
                                {cr.source_leave_type} exhausted → {cr.balance_leave_type || 'None'} ({cr.payroll_treatment}) ({cr.request_unit}:{cr.balance_unit})
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between items-center border-t border-gray-100 pt-4 mt-2">
                    {hasPermission('leave:policy:edit') && (
                      <button
                        onClick={() => handleEditPolicy(pol)}
                        className="text-xs font-bold text-blue-600 hover:text-blue-800 transition"
                      >
                        Edit Leave Policy
                      </button>
                    )}
                    {hasPermission('leave:policy:activate') && (
                      <button
                        onClick={() => handleTogglePolicyStatus(pol)}
                        className={`text-xs font-bold transition ${pol.status === 'active' ? 'text-amber-600 hover:text-amber-800' : 'text-green-600 hover:text-green-800'
                          }`}
                      >
                        {pol.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB 3: ASSIGNMENTS */}
      {activeTab === 'assignments' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Effective-Dated Leave Policy Assignments</h2>

          <form onSubmit={handleAssignPolicy} className="bg-gray-50 p-6 rounded-xl space-y-4 border border-gray-200">
            <h3 className="text-sm font-bold text-gray-800">Assign Leave Policy to Employees</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Select Leave Policy</label>
                <select
                  value={assignForm.policy_id}
                  onChange={(e) => {
                    setAssignForm({ ...assignForm, policy_id: e.target.value });
                    setError('');
                  }}
                  className={inputClass}
                >
                  <option value="">-- Choose Leave Policy --</option>
                  {policies.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Effective From Date</label>
                <input
                  type="date"
                  value={assignForm.effective_from}
                  onChange={(e) => setAssignForm({ ...assignForm, effective_from: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Assignment Scope</label>
                <div className="flex space-x-4 pt-2">
                  <label className="flex items-center space-x-2 text-sm">
                    <input
                      type="radio"
                      name="scope"
                      value="all"
                      checked={assignForm.mode === 'all'}
                      onChange={() => setAssignForm({ ...assignForm, mode: 'all' })}
                    />
                    <span>All Active Employees</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm">
                    <input
                      type="radio"
                      name="scope"
                      value="selected"
                      checked={assignForm.mode === 'selected'}
                      onChange={() => setAssignForm({ ...assignForm, mode: 'selected' })}
                    />
                    <span>Selected Employees</span>
                  </label>
                </div>
              </div>
            </div>

            {assignForm.mode === 'selected' && (
              <div className="space-y-2 border-t border-gray-200 pt-3">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-bold text-gray-700 uppercase">Select Target Employees ({assignForm.selected_emp_ids.length} selected):</label>
                  <input
                    type="text"
                    placeholder="Search employees..."
                    value={empSearch}
                    onChange={(e) => setEmpSearch(e.target.value)}
                    className="w-64 rounded-md border border-gray-200 px-2 py-1 text-xs"
                  />
                </div>

                <div className="max-h-48 overflow-y-auto border border-gray-200 bg-white rounded-lg p-2 space-y-1">
                  {filteredEmployees.map((emp) => {
                    const isChecked = assignForm.selected_emp_ids.includes(emp.id);
                    return (
                      <label key={emp.id} className="flex items-center space-x-3 p-1.5 hover:bg-gray-50 rounded text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAssignForm({ ...assignForm, selected_emp_ids: [...assignForm.selected_emp_ids, emp.id] });
                            } else {
                              setAssignForm({ ...assignForm, selected_emp_ids: assignForm.selected_emp_ids.filter(id => id !== emp.id) });
                            }
                          }}
                          className="rounded text-blue-600 h-4 w-4"
                        />
                        <span className="font-mono font-bold text-gray-900">{emp.emp_code}</span>
                        <span className="font-semibold">{emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`}</span>
                        <span className="text-gray-500">({emp.designation?.title || emp.emp_desig || 'Staff'})</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-blue-700"
              style={{ display: hasPermission('leave:policy:assign') ? 'block' : 'none' }}
            >
              {loading ? 'Assigning Leave Policy...' : 'Confirm Leave Policy Assignment & Initialize Balances'}
            </button>
          </form>

          {/* Assignment Filters */}
          <div className="flex flex-wrap gap-4 items-center bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search employee name or code..."
                value={assignmentFilter.search}
                onChange={(e) => setAssignmentFilter({ ...assignmentFilter, search: e.target.value })}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <select
                value={assignmentFilter.policy_id}
                onChange={(e) => setAssignmentFilter({ ...assignmentFilter, policy_id: e.target.value })}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="">All Policies</option>
                {policies.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={assignmentFilter.status}
                onChange={(e) => setAssignmentFilter({ ...assignmentFilter, status: e.target.value })}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Assignments Table */}
          <ScrollableTableContainer
            header={null}
            maxHeight={400}
            showTopScrollbar={true}
            stickyHeader={true}
          >
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-gray-700 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3">Employee Code</th>
                  <th className="px-4 py-3">Employee Name</th>
                  <th className="px-4 py-3">Assigned Leave Policy</th>
                  <th className="px-4 py-3">Effective From</th>
                  <th className="px-4 py-3">Effective To</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredAssignments.map((a) => (
                  <tr key={a.id}>
                    <td className="px-4 py-3 font-mono font-medium">{a.employee_code}</td>
                    <td className="px-4 py-3">{a.employee_name}</td>
                    <td className="px-4 py-3 font-bold text-blue-600">{a.policy_name}</td>
                    <td className="px-4 py-3">{a.effective_from}</td>
                    <td className="px-4 py-3">{a.effective_to || 'Current'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2.5 py-0.5 bg-green-100 text-green-800 text-xs font-bold rounded-full">{a.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollableTableContainer>
        </div>
      )}

      {/* DAY EDIT MODAL */}
      {editingDay && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900">Edit Calendar Day</h3>
              <button onClick={() => setEditingDay(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="text"
                  value={editingDay.calendar_date}
                  disabled
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-50 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Day Type *</label>
                <select
                  value={editingDay.day_type}
                  onChange={(e) => setEditingDay({ ...editingDay, day_type: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="WORKING_DAY">Working Day</option>
                  <option value="WEEK_OFF">Week Off</option>
                  <option value="HOLIDAY">Holiday</option>
                  <option value="OPTIONAL_HOLIDAY">Optional Holiday</option>
                  <option value="SPECIAL_WORKING_DAY">Special Working Day</option>
                </select>
              </div>

              {editingDay.day_type === 'HOLIDAY' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Holiday Name *</label>
                  <input
                    type="text"
                    value={editingDay.holiday_name || ''}
                    onChange={(e) => setEditingDay({ ...editingDay, holiday_name: e.target.value })}
                    placeholder="e.g., Independence Day"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  rows={2}
                  value={editingDay.remarks || ''}
                  onChange={(e) => setEditingDay({ ...editingDay, remarks: e.target.value })}
                  placeholder="Optional notes..."
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => setEditingDay(null)}
                className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateCalendarDay}
                disabled={loading}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FINALIZE CONFIRMATION MODAL */}
      {showFinalizeModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900">Finalize Calendar</h3>
              <button onClick={() => setShowFinalizeModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Are you sure you want to finalize the work calendar for <span className="font-bold">{new Date(0, calMonth - 1).toLocaleString('en', { month: 'long' })} {calYear}</span>?
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <p className="text-xs font-semibold text-amber-800">This action will:</p>
                <ul className="text-xs text-amber-700 list-disc pl-4 space-y-1">
                  <li>Lock the calendar from further edits</li>
                  <li>Make it authoritative for payroll and attendance calculations</li>
                  <li>Supersede any previous finalized calendar for this month</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">Working Days:</span>
                  <span className="font-bold text-gray-900">{currentCalendar?.total_working_days}</span>
                </div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">Week Offs:</span>
                  <span className="font-bold text-gray-900">{currentCalendar?.total_week_offs}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Holidays:</span>
                  <span className="font-bold text-gray-900">{currentCalendar?.total_holidays}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => setShowFinalizeModal(false)}
                className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleFinalizeCalendar}
                disabled={loading}
                className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-bold hover:bg-green-700 transition disabled:opacity-50"
              >
                {loading ? 'Finalizing...' : 'Confirm Finalize'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: HOLIDAY CALENDAR MANAGEMENT */}
      {activeTab === 'calendar' && <HolidayCalendarManagement />}

      {/* TAB 5: MARK ABSENTEE */}
      {activeTab === 'mark-absentee' && <MarkAbsenteeManagement />}

      {/* TAB 6: ABSENCE REGULARISATION */}
      {activeTab === 'absence' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Uncovered Absence Resolution & Regularisation</h2>

          {/* FILTER SECTION */}
          <div className="flex flex-wrap items-end gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200">
            <div className="flex-1 min-w-[150px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">Month</label>
              <select
                value={regMonth}
                onChange={(e) => setRegMonth(parseInt(e.target.value))}
                disabled={loading}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('en', { month: 'long' })}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[120px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
              <select
                value={regYear}
                onChange={(e) => setRegYear(parseInt(e.target.value))}
                disabled={loading}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {[2025, 2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">Employee</label>
              <select
                value={regEmpId}
                onChange={(e) => setRegEmpId(e.target.value)}
                disabled={loading}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="">-- Choose Employee --</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.full_name || `${e.first_name} ${e.last_name}`} ({e.emp_code})</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleCheckAbsence}
              disabled={loading || !regEmpId}
              className="bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ display: hasPermission('leave:absence:check') ? 'block' : 'none' }}
            >
              {loading ? 'Checking...' : 'Check Absence'}
            </button>
          </div>

          {/* MONTHLY SUMMARY */}
          {absenceData && (
            <div className="space-y-4 border-t border-gray-100 pt-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-blue-700 font-semibold">Total Days</p>
                  <p className="text-xl font-bold text-blue-900">{absenceData.total_days}</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <p className="text-xs text-orange-700 font-semibold">Absent Days</p>
                  <p className="text-xl font-bold text-orange-900">{absenceData.absent_days}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-xs text-green-700 font-semibold">Present Days</p>
                  <p className="text-xl font-bold text-green-900">{absenceData.present_days}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-purple-700 font-semibold">Paid Leave Days</p>
                  <p className="text-xl font-bold text-purple-900">{absenceData.paid_leave_days}</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-xs text-red-700 font-semibold">Uncovered Absent Days</p>
                  <p className="text-xl font-bold text-red-900">{Math.max(0, absenceData.absent_days - absenceData.paid_leave_days)}</p>
                </div>
              </div>

              {absenceData.absent_days > 0 ? (
                <>
                  {/* DATE SELECTION */}
                  <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <h3 className="font-bold text-gray-900 text-sm">Select Uncovered Absence Dates to Regularise</h3>
                      <div className="flex gap-2">
                        <button onClick={handleSelectAllDates} className="text-xs text-blue-600 hover:underline font-semibold">Select All</button>
                        <button onClick={handleClearDateSelection} className="text-xs text-gray-600 hover:underline">Clear Selection</button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">Selected: <span className="font-bold text-gray-900">{selectedAbsenceDates.length}</span> / {absenceData.absent_dates.length}</p>

                    <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-48 overflow-y-auto">
                      {absenceData.absent_dates.map((dateStr) => {
                        const dateObj = new Date(dateStr);
                        const weekday = dateObj.toLocaleDateString('en', { weekday: 'short' });
                        return (
                          <label key={dateStr} className="flex items-center space-x-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50 transition">
                            <input
                              type="checkbox"
                              checked={selectedAbsenceDates.includes(dateStr)}
                              onChange={() => handleToggleDateSelection(dateStr)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex flex-col">
                              <span className="text-xs font-semibold text-gray-900">{dateStr}</span>
                              <span className="text-[10px] text-gray-500">{weekday}</span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* LEAVE QUOTA SELECTION */}
                  {selectedAbsenceDates.length > 0 && (
                    <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                      <h3 className="font-bold text-gray-900 text-sm">Cover Using Leave Quota</h3>

                      {employeeBalances?.status === 'NO_ACTIVE_POLICY' && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-sm text-red-800 font-medium">{employeeBalances.message}</p>
                        </div>
                      )}

                      {employeeBalances?.status === 'POLICY_HAS_NO_ENTITLEMENTS' && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="text-sm text-amber-800 font-medium">{employeeBalances.message}</p>
                        </div>
                      )}

                      {employeeBalances?.status === 'BALANCE_NOT_INITIALIZED' && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="text-sm text-amber-800 font-medium">{employeeBalances.message}</p>
                        </div>
                      )}

                      {employeeBalances?.status === 'SUCCESS' && employeeBalances.balances && employeeBalances.balances.length > 0 ? (
                        <div className="space-y-2">
                          {employeeBalances.balances.map((balance) => (
                            <label
                              key={balance.id}
                              className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition ${regLeaveType === balance.leave_type_code ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                            >
                              <input
                                type="radio"
                                name="leaveType"
                                value={balance.leave_type_code}
                                checked={regLeaveType === balance.leave_type_code}
                                onChange={(e) => setRegLeaveType(e.target.value)}
                                className="text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1 ml-3">
                                <div className="flex items-center space-x-2">
                                  <span className="font-bold text-sm text-gray-900">{balance.leave_type_code}</span>
                                  <span className="text-sm text-gray-700">- {balance.leave_type_name}</span>
                                  <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{balance.period_type}</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="text-xs text-gray-500">Available:</span>
                                <span className="text-sm font-bold text-green-700 ml-1">{balance.balance || 0} days</span>
                              </div>
                            </label>
                          ))}
                        </div>
                      ) : employeeBalances?.status === 'SUCCESS' && (!employeeBalances.balances || employeeBalances.balances.length === 0) ? (
                        <p className="text-sm text-gray-500">No leave balances found for the applicable period.</p>
                      ) : !employeeBalances ? (
                        <p className="text-sm text-gray-500">Loading leave balances...</p>
                      ) : null}

                      {regLeaveType && employeeBalances?.status === 'SUCCESS' && employeeBalances.balances && (
                        <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Selected dates:</span>
                            <span className="font-bold text-gray-900">{selectedAbsenceDates.length}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Required balance:</span>
                            <span className="font-bold text-gray-900">{selectedAbsenceDates.length} days</span>
                          </div>
                          {(() => {
                            const selectedBalance = employeeBalances.balances.find(b => b.leave_type_code === regLeaveType);
                            const available = selectedBalance?.balance || 0;
                            const after = available - selectedAbsenceDates.length;
                            return (
                              <>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Available balance:</span>
                                  <span className="font-bold text-gray-900">{available} days</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Balance after regularisation:</span>
                                  <span className={`font-bold ${after >= 0 ? 'text-green-700' : 'text-red-700'}`}>{after} days</span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {/* REGULARISATION FORM */}
                  {selectedAbsenceDates.length > 0 && regLeaveType && (
                    <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                      <h3 className="font-bold text-gray-900 text-sm">Regularisation Details</h3>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Reason / Remarks *</label>
                        <textarea
                          rows={2}
                          value={regReason}
                          onChange={(e) => setRegReason(e.target.value)}
                          placeholder="Provide reason for regularisation..."
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <button
                        onClick={handleRegulariseAbsence}
                        disabled={loading || !regReason.trim()}
                        className="w-full bg-green-600 text-white px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ display: hasPermission('leave:absence:regularize') ? 'block' : 'none' }}
                      >
                        {loading ? 'Regularising...' : 'Regularise Selected Absence'}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-green-700 font-medium">✓ No uncovered absences detected for this employee in {new Date(0, regMonth - 1).toLocaleString('en', { month: 'long' })} {regYear}</p>
                </div>
              )}
            </div>
          )}

          {/* SUCCESS RESULT */}
          {regularisationResult && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
              <p className="text-sm text-green-800 font-medium">{regularisationResult.message}</p>
              {regularisationResult.leave_type_used && (
                <p className="text-xs text-green-700">Leave type used: <span className="font-bold">{regularisationResult.leave_type_used}</span></p>
              )}
              {regularisationResult.payroll_impact?.has_arrears && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                  <p className="text-xs text-amber-800 font-medium">⚠️ Payroll for the affected month(s) was already processed. A payroll adjustment has been added to Leave Arrears for processing.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* TAB 6.5: LEAVE ARREARS HISTORY */}
      {activeTab === 'arrears' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Leave Arrears History & Payout Tracking</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Track post-payroll attendance regularizations, penny-exact date deduction refunds, and monthly payroll credit status.
              </p>
            </div>
            <button
              onClick={fetchArrears}
              className="inline-flex items-center space-x-1.5 text-xs font-bold text-blue-600 bg-blue-50 px-3 py-2 rounded-lg hover:bg-blue-100 transition self-start"
            >
              <span>Refresh Arrears List</span>
            </button>
          </div>

          {/* METRIC SUMMARY CARDS */}
          {(() => {
            const pendingRecords = (arrears || []).filter(r => r.status === 'PENDING');
            const paidRecords = (arrears || []).filter(r => r.status === 'APPLIED');

            const totalPendingAmt = Math.round(pendingRecords.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0));
            const totalPaidAmt = Math.round(paidRecords.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0));
            const totalDaysRefunded = (arrears || []).reduce((sum, r) => sum + (parseFloat(r.days) || 0), 0);
            const uniqueEmployees = new Set((arrears || []).map(r => r.employee_id)).size;
            const employeesWithPending = new Set(pendingRecords.map(r => r.employee_id)).size;
            const pendingGroupsCount = groupedArrears.filter(g => g.monthly_status === 'PENDING' || g.monthly_status === 'PARTIALLY_PAID').length;
            const paidGroupsCount = groupedArrears.filter(g => g.monthly_status === 'PAID').length;

            return (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50/50 p-4 rounded-xl border border-amber-200/60 shadow-sm space-y-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-amber-800">Pending Payroll Credits</span>
                  <div className="flex items-baseline justify-between pt-1">
                    <span className="text-2xl font-black text-amber-950">₹{totalPendingAmt.toLocaleString('en-IN')}</span>
                    <span className="text-xs font-bold bg-amber-200/80 text-amber-900 px-2 py-0.5 rounded-full">{pendingGroupsCount} Groups</span>
                  </div>
                  <p className="text-[11px] text-amber-700/90 pt-1">Will be automatically credited in the next open payroll run.</p>
                </div>

                <div className="bg-gradient-to-br from-emerald-50 to-teal-50/50 p-4 rounded-xl border border-emerald-200/60 shadow-sm space-y-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-800">Processed & Paid Arrears</span>
                  <div className="flex items-baseline justify-between pt-1">
                    <span className="text-2xl font-black text-emerald-950">₹{totalPaidAmt.toLocaleString('en-IN')}</span>
                    <span className="text-xs font-bold bg-emerald-200/80 text-emerald-900 px-2 py-0.5 rounded-full">{paidGroupsCount} Groups</span>
                  </div>
                  <p className="text-[11px] text-emerald-700/90 pt-1">Fully processed and credited into employee salary slips.</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50/50 p-4 rounded-xl border border-blue-200/60 shadow-sm space-y-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-800">Total Regularized Days</span>
                  <div className="flex items-baseline justify-between pt-1">
                    <span className="text-2xl font-black text-blue-950">{totalDaysRefunded} Day(s)</span>
                    <span className="text-xs font-bold bg-blue-200/80 text-blue-900 px-2 py-0.5 rounded-full">{uniqueEmployees} Employees</span>
                  </div>
                  <p className="text-[11px] text-blue-700/90 pt-1">Total post-payroll absence days regularized into leave arrears.</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50/50 p-4 rounded-xl border border-purple-200/60 shadow-sm space-y-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-purple-800">Employees with Pending</span>
                  <div className="flex items-baseline justify-between pt-1">
                    <span className="text-2xl font-black text-purple-950">{employeesWithPending}</span>
                    <span className="text-xs font-bold bg-purple-200/80 text-purple-900 px-2 py-0.5 rounded-full">Pending Payouts</span>
                  </div>
                  <p className="text-[11px] text-purple-700/90 pt-1">Unique employees with pending arrear payouts.</p>
                </div>
              </div>
            );
          })()}

          {/* SEARCH & STATUS FILTER */}
          <div className="flex flex-wrap gap-4 items-center bg-gray-50 p-4 rounded-xl border border-gray-200">
            <div className="flex-1 min-w-[240px]">
              <input
                type="text"
                placeholder="Search employee code, name, payroll month/year, or regularization reason..."
                value={arrearSearch}
                onChange={(e) => setArrearSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
            <div className="w-40">
              <select
                value={arrearStatusFilter}
                onChange={(e) => setArrearStatusFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="ALL">All Statuses</option>
                <option value="PENDING">Pending Payout</option>
                <option value="PAID">Paid in Payroll</option>
                <option value="PARTIALLY_PAID">Partially Paid</option>
              </select>
            </div>
            <div className="w-32">
              <select
                value={arrearMonthFilter}
                onChange={(e) => setArrearMonthFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="ALL">All Months</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('en', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-32">
              <select
                value={arrearYearFilter}
                onChange={(e) => setArrearYearFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="ALL">All Years</option>
                {[2024, 2025, 2026, 2027, 2028].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {/* LEAVE ARREARS TABLE */}
          {loading ? (
            <div className="py-8 text-center text-sm text-gray-500">Loading leave arrears history...</div>
          ) : (
            <ScrollableTableContainer header={null} maxHeight={500} showTopScrollbar={true} stickyHeader={true}>
              <table className="w-full text-left text-sm divide-y divide-gray-200">
                <thead className="bg-gray-50 text-gray-700 font-bold uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3">Employee Details</th>
                    <th className="px-4 py-3">Payroll Month</th>
                    <th className="px-4 py-3 text-center">Total Regularized Days</th>
                    <th className="px-4 py-3">Absence Period / Dates</th>
                    <th className="px-4 py-3 text-right">Total Refundable Amount (₹)</th>
                    <th className="px-4 py-3">Deducted In Payroll</th>
                    <th className="px-4 py-3">Payout Status</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {groupedArrears
                    .filter(group => {
                      const searchStr = (arrearSearch || '').toLowerCase();
                      const code = (group.employee_code || '').toLowerCase();
                      const name = (group.employee_name || '').toLowerCase();
                      const monthYear = group.payroll_month && group.payroll_year
                        ? `${new Date(0, group.payroll_month - 1).toLocaleString('en', { month: 'long' })} ${group.payroll_year}`.toLowerCase()
                        : '';
                      const reason = group.records.map(r => r.reason_reference || '').join(' ').toLowerCase();

                      const matchesSearch = !searchStr ||
                        code.includes(searchStr) ||
                        name.includes(searchStr) ||
                        monthYear.includes(searchStr) ||
                        reason.includes(searchStr);

                      const matchesStatus = arrearStatusFilter === 'ALL' || group.monthly_status === arrearStatusFilter;
                      const matchesMonth = arrearMonthFilter === 'ALL' || group.payroll_month === parseInt(arrearMonthFilter);
                      const matchesYear = arrearYearFilter === 'ALL' || group.payroll_year === parseInt(arrearYearFilter);

                      return matchesSearch && matchesStatus && matchesMonth && matchesYear;
                    })
                    .map((group) => {
                      const isPending = group.monthly_status === 'PENDING';
                      const isPartiallyPaid = group.monthly_status === 'PARTIALLY_PAID';
                      const isPaid = group.monthly_status === 'PAID';

                      // Format dates display
                      const allDates = group.records.flatMap(r => r.dates || (r.date_breakdown || []).map(d => d.date));
                      const uniqueDates = [...new Set(allDates)].sort();
                      let datesDisplay = '';

                      if (uniqueDates.length > 0) {
                        const firstDate = new Date(uniqueDates[0]);
                        const lastDate = new Date(uniqueDates[uniqueDates.length - 1]);
                        const isContinuous = uniqueDates.length === (lastDate - firstDate) / (1000 * 60 * 60 * 24) + 1;

                        if (isContinuous && uniqueDates.length > 1) {
                          datesDisplay = `${formatDate(firstDate)} – ${formatDate(lastDate)}`;
                        } else {
                          datesDisplay = `${uniqueDates.length} Selected Dates`;
                        }
                      } else {
                        datesDisplay = `${group.total_days} Day(s)`;
                      }

                      return (
                        <tr key={`${group.employee_id}_${group.payroll_month}_${group.payroll_year}`} className="hover:bg-blue-50/30 transition">
                          <td className="px-4 py-3.5">
                            <div className="font-bold text-gray-900">{group.employee_name}</div>
                            <div className="text-xs text-gray-500 font-mono">{group.employee_code}</div>
                          </td>
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            {group.payroll_month && group.payroll_year ? (
                              <span className="text-xs font-semibold text-gray-800 bg-gray-100 px-2 py-1 rounded">
                                {new Date(0, group.payroll_month - 1).toLocaleString('en', { month: 'long' })} {group.payroll_year}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">Historical</span>
                            )}
                          </td>
                          <td className="px-4 py-3.5 text-center font-bold text-blue-900">
                            {group.total_days} Day(s)
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="font-medium text-xs text-gray-800">{datesDisplay}</div>
                          </td>
                          <td className="px-4 py-3.5 text-right whitespace-nowrap">
                            <div className="font-black text-emerald-700 text-base">
                              ₹{Math.round(group.total_amount).toLocaleString('en-IN')}
                            </div>
                          </td>
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            {group.payroll_month && group.payroll_year ? (
                              <span className="text-xs font-semibold text-gray-800 bg-gray-100 px-2 py-1 rounded">
                                {new Date(0, group.payroll_month - 1).toLocaleString('en', { month: 'short' })} {group.payroll_year} Payroll
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">Historical Payroll</span>
                            )}
                          </td>
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            {isPending ? (
                              <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200/60">
                                ⏳ Pending Payout
                              </span>
                            ) : isPartiallyPaid ? (
                              <span className="text-xs font-semibold text-orange-700 bg-orange-50 px-2 py-1 rounded border border-orange-200/60">
                                ⚠️ Partially Paid
                              </span>
                            ) : (
                              <span className="text-xs font-semibold text-emerald-800 bg-emerald-50 px-2 py-1 rounded border border-emerald-200/60">
                                ✓ Paid in Payroll
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3.5 text-center whitespace-nowrap">
                            <button
                              onClick={() => setSelectedArrearDetail(group)}
                              className="px-2.5 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 font-bold text-xs rounded border border-blue-200 transition"
                            >
                              View Breakdown
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                  {groupedArrears.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-gray-500 italic">
                        No leave arrears recorded yet. Post-payroll regularizations will automatically create leave arrear records here.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </ScrollableTableContainer>
          )}

          {/* LEAVE ARREAR DETAIL BREAKDOWN MODAL */}
          {selectedArrearDetail && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
              <div className="w-full max-w-4xl bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100 space-y-0 my-8">
                <div className="px-6 py-4 bg-gradient-to-r from-blue-900 to-indigo-900 text-white flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-lg">Monthly Arrear Breakdown</h3>
                    <p className="text-xs text-blue-200">
                      {selectedArrearDetail.employee_name} ({selectedArrearDetail.employee_code}) - {selectedArrearDetail.payroll_month && selectedArrearDetail.payroll_year ? `${new Date(0, selectedArrearDetail.payroll_month - 1).toLocaleString('en', { month: 'long' })} ${selectedArrearDetail.payroll_year}` : 'Historical'}
                    </p>
                  </div>
                  <button onClick={() => setSelectedArrearDetail(null)} className="text-blue-200 hover:text-white transition">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto text-xs">
                  {/* Monthly Summary Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <div>
                      <span className="text-gray-500 block uppercase font-semibold text-[10px]">Employee</span>
                      <span className="font-bold text-gray-900 text-sm">{selectedArrearDetail.employee_name}</span>
                      <span className="text-gray-500 block font-mono">{selectedArrearDetail.employee_code}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block uppercase font-semibold text-[10px]">Total Refund Amount</span>
                      <span className="font-black text-emerald-600 text-lg">
                        ₹{Math.round(selectedArrearDetail.total_amount).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block uppercase font-semibold text-[10px]">Total Days</span>
                      <span className="font-black text-blue-600 text-lg">{selectedArrearDetail.total_days} Day(s)</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block uppercase font-semibold text-[10px]">Payout Status</span>
                      <span className={`font-bold text-sm ${selectedArrearDetail.monthly_status === 'PENDING' ? 'text-amber-600' :
                        selectedArrearDetail.monthly_status === 'PARTIALLY_PAID' ? 'text-orange-600' :
                          'text-emerald-600'
                        }`}>
                        {selectedArrearDetail.monthly_status === 'PENDING' ? 'Pending Payout' :
                          selectedArrearDetail.monthly_status === 'PARTIALLY_PAID' ? 'Partially Paid' :
                            'Paid in Payroll'}
                      </span>
                    </div>
                  </div>

                  {/* Individual Records Table */}
                  <div className="space-y-2">
                    <h4 className="font-bold text-gray-800 uppercase tracking-wider text-[10px]">Individual Arrear Records ({selectedArrearDetail.records.length})</h4>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-gray-100 text-gray-700 font-bold uppercase text-[10px]">
                          <tr>
                            <th className="px-3 py-2">Arrear ID</th>
                            <th className="px-3 py-2">Regularized Date(s)</th>
                            <th className="px-3 py-2">Days</th>
                            <th className="px-3 py-2 text-right">Refundable Amount (₹)</th>
                            <th className="px-3 py-2">Reason</th>
                            <th className="px-3 py-2">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {selectedArrearDetail.records.map((record, idx) => (
                            <tr key={record.id || idx} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-mono font-medium text-gray-800">#{record.id}</td>
                              <td className="px-3 py-2">
                                {record.dates && record.dates.length > 0 ? (
                                  <div className="text-[10px] text-gray-600">
                                    {record.dates.length > 1 ? `${record.dates.length} dates` : record.dates[0]}
                                  </div>
                                ) : record.date_breakdown && record.date_breakdown.length > 0 ? (
                                  <div className="text-[10px] text-gray-600">
                                    {record.date_breakdown.length > 1 ? `${record.date_breakdown.length} dates` : record.date_breakdown[0].date}
                                  </div>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-3 py-2 font-medium text-gray-800">{record.days}</td>
                              <td className="px-3 py-2 text-right font-bold text-emerald-700">
                                ₹{Math.round(parseFloat(record.amount || 0)).toLocaleString('en-IN')}
                              </td>
                              <td className="px-3 py-2 text-gray-600 max-w-[200px] truncate" title={record.reason_reference}>
                                {record.reason_reference || '-'}
                              </td>
                              <td className="px-3 py-2">
                                <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase ${record.status === 'PENDING' ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-900'
                                  }`}>
                                  {record.status === 'PENDING' ? 'Pending' : 'Paid'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Detailed Date Breakdown for Selected Records */}
                  <div className="space-y-2">
                    <h4 className="font-bold text-gray-800 uppercase tracking-wider text-[10px]">All Regularized Dates</h4>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-gray-100 text-gray-700 font-bold uppercase text-[10px]">
                          <tr>
                            <th className="px-3 py-2">Regularized Date</th>
                            <th className="px-3 py-2 text-right">Deducted Amount (₹)</th>
                            <th className="px-3 py-2">Arrear Reference</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {selectedArrearDetail.records.flatMap((record, rIdx) => {
                            const dates = record.date_breakdown || [];
                            if (dates.length === 0 && record.dates && record.dates.length > 0) {
                              return record.dates.map((date, dIdx) => ({
                                date,
                                amount: record.daily_rate || (record.amount / record.days),
                                arrearId: record.id
                              }));
                            }
                            return dates.map((d, dIdx) => ({
                              date: d.date,
                              amount: d.amount,
                              arrearId: record.id
                            }));
                          }).sort((a, b) => new Date(a.date) - new Date(b.date)).map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-mono font-medium text-gray-800">{item.date}</td>
                              <td className="px-3 py-2 text-right font-bold text-emerald-700">₹{Math.round(parseFloat(item.amount || 0)).toLocaleString('en-IN')}</td>
                              <td className="px-3 py-2 text-gray-500">#{item.arrearId}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
                  <button onClick={() => setSelectedArrearDetail(null)} className="px-4 py-2 rounded-lg bg-gray-900 text-white font-bold text-xs hover:bg-black transition">
                    Close Breakdown
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 7: LEAVE SHEET */}
      {activeTab === 'sheet' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">

          <div className="flex items-center space-x-4 bg-gray-50 p-4 rounded-xl border border-gray-200">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Select Employee</label>
              <select value={sheetEmpId} onChange={(e) => setSheetEmpId(e.target.value)} className={inputClass}>
                <option value="">-- Choose Employee --</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.full_name || `${e.first_name} ${e.last_name}`} ({e.emp_code})</option>
                ))}
              </select>
            </div>
            <button onClick={handleFetchSheet} className="bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold mt-5 hover:bg-blue-700"
              style={{ display: hasPermission('leave:sheet:generate') ? 'block' : 'none' }}
            >
              Generate Leave Sheet
            </button>
          </div>

          {sheetData && (
            <div className="space-y-6 border-t border-gray-100 pt-4">
              <LeaveReportView sheetData={sheetData} refreshKey={sheetRefreshKey} />
            </div>
          )}
        </div>
      )}

      {/* APPROVAL ACTION MODAL */}
      {appActionModal.open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl p-6 space-y-4">
            <h3 className="font-bold text-gray-900 text-lg">
              Confirm Action: <span className="uppercase text-blue-600">{(appActionModal.type === 'checker' && isAdmin) ? 'approve' : appActionModal.type}</span>
            </h3>
            <p className="text-xs text-gray-500">Application Number: <span className="font-mono font-bold text-gray-800">{appActionModal.app?.application_number}</span></p>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Remarks{appActionModal.type === 'reject' ? ' *' : ''}</label>
              <textarea
                rows={3}
                value={appActionModal.remarks}
                onChange={(e) => setAppActionModal({ ...appActionModal, remarks: e.target.value })}
                className={inputClass}
                placeholder="Enter approval/rejection remarks..."
              />
            </div>

            <div className="flex justify-end space-x-3 border-t border-gray-100 pt-3">
              <button onClick={() => setAppActionModal({ open: false, type: '', app: null, remarks: '' })} className="px-4 py-2 rounded-lg border text-sm">Cancel</button>
              <button onClick={handleAppAction} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700">Confirm Action</button>
            </div>
          </div>
        </div>
      )}

      {/* LEAVE DETAILS MODAL */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl p-6 space-y-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900 text-lg">Leave Application Details</h3>
              <button onClick={() => setSelectedApp(null)} className="text-gray-400 hover:text-gray-600 transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase">Application No</p>
                  <p className="text-sm font-mono font-bold text-gray-800">{selectedApp.application_number}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase">Status</p>
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold ${STATUS_BADGE[selectedApp.status] || 'bg-gray-100 text-gray-800'}`}>
                    {formatStatus(selectedApp.status)}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase">Employee</p>
                  <p className="text-sm font-medium text-gray-800">{selectedApp.employee_name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase">Employee Code</p>
                  <p className="text-sm font-medium text-gray-800">{selectedApp.employee_code || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase">Leave Type</p>
                  <p className="text-sm font-medium text-gray-800">{selectedApp.leave_type_name || selectedApp.leave_type_code || 'Leave'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase">Total Days</p>
                  <p className="text-sm font-bold text-gray-800">{selectedApp.no_of_days}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase">From Date</p>
                  <p className="text-sm font-medium text-gray-800">{selectedApp.from_date}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase">To Date</p>
                  <p className="text-sm font-medium text-gray-800">{selectedApp.to_date}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Employee Reason</p>
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 border border-gray-100 whitespace-pre-wrap">
                  {selectedApp.reason || '-'}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase">Applied On</p>
                  <p className="text-sm font-medium text-gray-800">
                    {selectedApp.applied_at ? new Date(selectedApp.applied_at).toLocaleString() : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase">Submitted By</p>
                  <p className="text-sm font-medium text-gray-800">{selectedApp.employee_name || 'Unknown'}</p>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 space-y-4">
                <h4 className="font-bold text-gray-900 text-sm border-l-4 border-blue-500 pl-2">WORKFLOW / APPROVAL DETAILS</h4>

                {selectedApp.status === 'rejected' ? (
                  <div className="space-y-3 bg-red-50/50 p-4 rounded-xl border border-red-100">
                    <div>
                      <p className="text-xs font-semibold text-red-800/80 uppercase">Rejected By</p>
                      <p className="text-sm font-bold text-red-900">{selectedApp.rejected_by_name || 'HR Administrator'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-red-800/80 uppercase mb-1">Rejection Remarks</p>
                      <p className="text-sm text-red-900 bg-white rounded-lg p-2.5 border border-red-100/50">{selectedApp.rejection_reason || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-red-800/80 uppercase">Rejected On</p>
                      <p className="text-sm font-medium text-red-900">
                        {selectedApp.rejected_at ? new Date(selectedApp.rejected_at).toLocaleString() : '-'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                    {selectedApp.maker_id && (
                      <div className="space-y-2 border-b border-gray-200/50 pb-2">
                        <div className="flex justify-between">
                          <span className="text-xs font-semibold text-gray-500">Maker Reviewer:</span>
                          <span className="text-xs font-bold text-gray-800">{selectedApp.maker_name || 'Unknown'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs font-semibold text-gray-500">Maker Remarks:</span>
                          <span className="text-xs text-gray-700 font-medium">{selectedApp.maker_remarks || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs font-semibold text-gray-500">Action Date:</span>
                          <span className="text-xs text-gray-500">
                            {selectedApp.maker_action_at ? new Date(selectedApp.maker_action_at).toLocaleString() : '-'}
                          </span>
                        </div>
                      </div>
                    )}

                    {selectedApp.checker_id && (
                      <div className="space-y-2 border-b border-gray-200/50 pb-2">
                        <div className="flex justify-between">
                          <span className="text-xs font-semibold text-gray-500">Reviewed / Approved By:</span>
                          <span className="text-xs font-bold text-gray-800">{selectedApp.checker_name || 'Unknown'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs font-semibold text-gray-500">Approval Remarks:</span>
                          <span className="text-xs text-gray-700 font-medium">{selectedApp.checker_remarks || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs font-semibold text-gray-500">Action Date:</span>
                          <span className="text-xs text-gray-500">
                            {selectedApp.checker_action_at ? new Date(selectedApp.checker_action_at).toLocaleString() : '-'}
                          </span>
                        </div>
                      </div>
                    )}

                    {selectedApp.hq_approver_id && (
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-xs font-semibold text-gray-500">HQ / Final Approver:</span>
                          <span className="text-xs font-bold text-gray-800">{selectedApp.hq_approver_name || 'Unknown'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs font-semibold text-gray-500">HQ Remarks:</span>
                          <span className="text-xs text-gray-700 font-medium">{selectedApp.hq_remarks || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs font-semibold text-gray-500">HQ Action Date:</span>
                          <span className="text-xs text-gray-500">
                            {selectedApp.hq_action_at ? new Date(selectedApp.hq_action_at).toLocaleString() : '-'}
                          </span>
                        </div>
                      </div>
                    )}

                    {!selectedApp.maker_id && !selectedApp.checker_id && !selectedApp.hq_approver_id && (
                      <p className="text-xs text-gray-500 text-center italic">No workflow history available (Pending maker review).</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end border-t border-gray-100 pt-3">
              <button onClick={() => setSelectedApp(null)} className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-800">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* POLICY CONFIG MODAL */}
      {showPolicyModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-5xl bg-white rounded-xl shadow-xl p-6 space-y-6 my-8 animate-in fade-in duration-200 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-900 text-lg">
                {editingPolicyId ? 'Edit Leave Policy' : 'Create Leave Policy'}
              </h3>
              <button
                onClick={() => { setShowPolicyModal(false); setEditingPolicyId(null); setShowLeaveTypeSelector(false); }}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSavePolicy} className="space-y-6">
              {/* POLICY DETAILS */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Leave Policy Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Leave Policy Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. General Staff Policy"
                      value={policyForm.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        const code = name.toUpperCase().replace(/[^A-Z0-9]/g, '_');
                        setPolicyForm({ ...policyForm, name, code: editingPolicyId ? policyForm.code : code });
                      }}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Leave Policy Code</label>
                    <input
                      type="text"
                      disabled={!!editingPolicyId}
                      placeholder="Auto-generated from name"
                      value={policyForm.code}
                      onChange={(e) => setPolicyForm({ ...policyForm, code: e.target.value })}
                      className={inputClass + ' bg-gray-100'}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Effective From *</label>
                    <input
                      type="date"
                      required
                      value={policyForm.effective_from}
                      onChange={(e) => setPolicyForm({ ...policyForm, effective_from: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Effective To</label>
                    <input
                      type="date"
                      value={policyForm.effective_to}
                      onChange={(e) => setPolicyForm({ ...policyForm, effective_to: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
                  <textarea
                    rows={2}
                    placeholder="Enter policy description..."
                    value={policyForm.description}
                    onChange={(e) => setPolicyForm({ ...policyForm, description: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* APPLY POLICY TO */}
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <h4 className="text-sm font-bold text-gray-800">Apply Leave Policy To</h4>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={policyForm.assign_all}
                      onChange={(e) => setPolicyForm({ ...policyForm, assign_all: e.target.checked })}
                      className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                    />
                    <span>Apply to All Active Employees</span>
                  </label>
                </div>

                {!policyForm.assign_all && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-bold text-gray-700 uppercase">
                        Select Target Employees ({policyForm.assigned_employee_ids.length} selected):
                      </label>
                      <input
                        type="text"
                        placeholder="Search employees by name or code..."
                        value={empSearch}
                        onChange={(e) => setEmpSearch(e.target.value)}
                        className="w-64 rounded-lg border border-gray-200 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="max-h-40 overflow-y-auto border border-gray-200 bg-white rounded-lg p-2 space-y-1">
                      {filteredEmployees.map((emp) => {
                        const isChecked = policyForm.assigned_employee_ids.includes(emp.id);
                        return (
                          <label key={emp.id} className="flex items-center space-x-3 p-1.5 hover:bg-gray-50 rounded text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                let newIds = [...policyForm.assigned_employee_ids];
                                if (isChecked) {
                                  newIds = newIds.filter(id => id !== emp.id);
                                } else {
                                  newIds.push(emp.id);
                                }
                                setPolicyForm({ ...policyForm, assigned_employee_ids: newIds });
                              }}
                              className="rounded text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                            />
                            <span className="font-medium text-gray-800">
                              {emp.full_name || `${emp.first_name} ${emp.last_name}`} ({emp.emp_code})
                            </span>
                          </label>
                        );
                      })}
                      {filteredEmployees.length === 0 && (
                        <p className="text-xs text-gray-500 text-center py-2">No matching employees found.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* LEAVE ENTITLEMENTS */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Leave Entitlements</h4>
                  <button
                    type="button"
                    onClick={handleAddEntitlementRow}
                    className="flex items-center space-x-1 text-xs font-semibold text-blue-600 hover:text-blue-800 transition"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Leave Type</span>
                  </button>
                </div>

                <ScrollableTableContainer
                  header={null}
                  maxHeight={400}
                  showTopScrollbar={true}
                  stickyHeader={true}
                >
                  <table className="w-full text-left text-xs divide-y divide-gray-200">
                    <thead className="bg-gray-50 text-gray-700 font-bold uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Leave Type</th>
                        <th className="px-4 py-3">Allocation Type</th>
                        <th className="px-4 py-3 text-center">Jan-Jun</th>
                        <th className="px-4 py-3 text-center">Jul-Dec</th>
                        <th className="px-4 py-3 text-center">Annual</th>
                        <th className="px-4 py-3">Paid</th>
                        <th className="px-4 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {getUniqueLeaveTypes(policyForm.allocation_rules).map((lt) => {
                        const janJunRule = policyForm.allocation_rules.find(r => r.leave_type === lt && r.allocation_period === 'JAN_JUN');
                        const julDecRule = policyForm.allocation_rules.find(r => r.leave_type === lt && r.allocation_period === 'JUL_DEC');
                        const annualRule = policyForm.allocation_rules.find(r => r.leave_type === lt && r.allocation_period === 'ANNUAL');
                        const allocationType = getAllocationTypeForLeaveType(lt);

                        return (
                          <tr key={lt} className="hover:bg-gray-50/50">
                            <td className="px-4 py-3 font-semibold text-gray-900">{lt}</td>

                            {/* Allocation Type */}
                            <td className="px-4 py-3">
                              <select
                                value={allocationType}
                                onChange={(e) => handleEntitlementAllocationTypeChange(lt, e.target.value)}
                                className="rounded border border-gray-200 px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="HALF_YEARLY">Half-Yearly</option>
                                <option value="ANNUAL">Annual</option>
                              </select>
                            </td>

                            {/* Jan-Jun */}
                            <td className="px-4 py-3 text-center">
                              <input
                                type="number"
                                min="0"
                                disabled={allocationType === 'ANNUAL'}
                                value={janJunRule ? janJunRule.allocation_days : ''}
                                onChange={(e) => handleEntitlementDayChange(lt, 'JAN_JUN', e.target.value)}
                                placeholder="-"
                                className="w-16 rounded border border-gray-200 px-2 py-1 text-center text-xs focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                              />
                            </td>

                            {/* Jul-Dec */}
                            <td className="px-4 py-3 text-center">
                              <input
                                type="number"
                                min="0"
                                disabled={allocationType === 'ANNUAL'}
                                value={julDecRule ? julDecRule.allocation_days : ''}
                                onChange={(e) => handleEntitlementDayChange(lt, 'JUL_DEC', e.target.value)}
                                placeholder="-"
                                className="w-16 rounded border border-gray-200 px-2 py-1 text-center text-xs focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                              />
                            </td>

                            {/* Annual */}
                            <td className="px-4 py-3 text-center">
                              <input
                                type="number"
                                min="0"
                                disabled={allocationType === 'HALF_YEARLY'}
                                value={annualRule ? annualRule.allocation_days : ''}
                                onChange={(e) => handleEntitlementDayChange(lt, 'ANNUAL', e.target.value)}
                                placeholder="-"
                                className="w-16 rounded border border-gray-200 px-2 py-1 text-center text-xs focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                              />
                            </td>

                            {/* Paid Status */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <label className="flex items-center space-x-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={janJunRule ? janJunRule.is_paid : (annualRule ? annualRule.is_paid : true)}
                                  onChange={(e) => handleEntitlementFlagChange(lt, 'is_paid', e.target.checked)}
                                  className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                                />
                                <span className="text-xs">Paid</span>
                              </label>
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => handleRemoveEntitlementType(lt)}
                                className="text-red-600 hover:text-red-800 text-xs font-semibold hover:bg-red-50 px-2.5 py-1 rounded"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {getUniqueLeaveTypes(policyForm.allocation_rules).length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-4 py-6 text-center text-gray-500 italic">
                            No entitlements configured. Click "Add Leave Type" to set allocation rules.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </ScrollableTableContainer>
              </div>

              {/* Leave Type Selector Modal */}
              {showLeaveTypeSelector && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
                    <h4 className="font-bold text-gray-900">Select Leave Type</h4>
                    <div className="space-y-2">
                      {['EL', 'HPL', 'CL', 'SL', 'PL', 'MTL', 'EOL'].map(lt => (
                        <button
                          key={lt}
                          type="button"
                          onClick={() => handleSelectLeaveType(lt)}
                          className="w-full text-left px-4 py-2 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition"
                        >
                          {lt} - {lt === 'EL' ? 'Earned Leave' : (lt === 'HPL' || lt === 'ML') ? 'Half Pay Leave' : lt === 'CL' ? 'Casual Leave' : lt === 'SL' ? 'Special Leave' : lt === 'PL' ? 'Paternity Leave' : lt === 'MTL' ? 'Maternity Leave' : 'Extraordinary Leave'}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowLeaveTypeSelector(false)}
                      className="w-full px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* POLICY LIMITS */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Leave Policy Limits</h4>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Maximum Accumulated Leave Balance</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="0"
                      value={policyForm.max_accumulated_leave_balance}
                      onChange={(e) => setPolicyForm({ ...policyForm, max_accumulated_leave_balance: parseInt(e.target.value) || 0 })}
                      className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600">Days</span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">Maximum total accumulated leave balance permitted under this policy.</p>
                </div>
              </div>

              {/* LEAVE CONVERSION / EXHAUSTION RULES */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <div className="space-y-0.5">
                    <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Leave Conversion Rules (System-Defined)</h4>
                    <p className="text-[11px] text-gray-500">Fixed system conversion rules and linked balance rules enforced across all policies.</p>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3 text-xs text-gray-700">
                  <div className="flex items-start space-x-2">
                    <span className="inline-block px-2 py-0.5 text-[11px] font-bold bg-blue-100 text-blue-800 rounded font-mono">RULE 1</span>
                    <div>
                      <span className="font-bold text-gray-900">Earned Leave (EL) Exhaustion Cascade:</span>
                      <p className="text-gray-600 mt-0.5"><span className="font-semibold text-gray-800">EL</span> &rarr; <span className="font-semibold text-gray-800">HPL</span> (Half Pay) &rarr; <span className="font-semibold text-gray-800">EOL</span> (Extraordinary Leave / Unpaid)</p>
                      <p className="text-[11px] text-gray-500 italic mt-0.5">When EL balance is exhausted, remaining days consume HPL (calculated at half pay). If HPL is also exhausted, remaining days become EOL (unpaid full-day deduction and non-qualifying service).</p>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-3 flex items-start space-x-2">
                    <span className="inline-block px-2 py-0.5 text-[11px] font-bold bg-purple-100 text-purple-800 rounded font-mono">RULE 2</span>
                    <div>
                      <span className="font-bold text-gray-900">Half Pay Leave (HPL) Exhaustion Cascade:</span>
                      <p className="text-gray-600 mt-0.5"><span className="font-semibold text-gray-800">HPL</span> &rarr; <span className="font-semibold text-gray-800">EL</span> &rarr; <span className="font-semibold text-gray-800">EOL</span> (Extraordinary Leave / Unpaid)</p>
                      <p className="text-[11px] text-gray-500 italic mt-0.5">When HPL is exhausted, remaining days consume available EL. If both HPL and EL are exhausted, remaining days become EOL.</p>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-3 flex items-start space-x-2">
                    <span className="inline-block px-2 py-0.5 text-[11px] font-bold bg-amber-100 text-amber-800 rounded font-mono">RULE 3</span>
                    <div>
                      <span className="font-bold text-gray-900">Semi-Annual EOL Deduction (Statutory Rule):</span>
                      <p className="text-gray-600 mt-0.5 font-mono text-[11px]"><span className="font-bold text-gray-800">EL &rarr; 1/10</span> | <span className="font-bold text-gray-800">HPL &rarr; 1/18</span> (Deducted against EOL)</p>
                      <p className="text-[11px] text-gray-500 italic mt-0.5">Every January: Deduct 1/10th of EL and 1/18th of HPL for previous year's JUL_DEC period.<br />Every July: Deduct 1/10th of EL and 1/18th of HPL for current year's JAN_JUN period.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Buttons */}
              <div className="flex justify-end space-x-3 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowPolicyModal(false); setEditingPolicyId(null); }}
                  className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Leave Policy'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ADMIN CREATE LEAVE REQUEST MODAL */}
      {showAdminCreateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-xl border border-gray-100 shadow-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h3 className="font-bold text-gray-900">Create Leave Request (Admin)</h3>
              <button onClick={() => setShowAdminCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateLeaveRequest}>
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Error Banner */}
                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-xs font-semibold">
                    {error}
                  </div>
                )}

                {/* Select Employee */}
                <div>
                  <SearchableSelect
                    label="Select Employee"
                    required
                    value={adminLeaveForm.employee_id}
                    onChange={(val) => setAdminLeaveForm({ ...adminLeaveForm, employee_id: val })}
                    options={employeeOptions}
                    placeholder="Search by name or code..."
                  />
                </div>

                {adminLeaveForm.employee_id && (
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 space-y-2">
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase block">Assigned Leave Policy</span>
                      <span className={`text-sm font-bold ${policyError === 'NO_POLICY' ? 'text-red-600' : 'text-gray-900'}`}>
                        {policyError === 'NO_POLICY' ? '⚠ No Leave Policy Assigned' : `[ ${employeePolicy?.name || 'Loading policy...'} ]`}
                      </span>
                    </div>

                    {policyError === 'NO_POLICY' && (
                      <div className="text-xs text-red-700 bg-red-50 p-2.5 rounded border border-red-100 font-medium">
                        ⚠ <strong>No Leave Policy Assigned:</strong> This employee does not have an active leave policy assigned. Assign a leave policy before creating a leave request.
                      </div>
                    )}

                    {policyError === 'NO_LEAVE_TYPES' && (
                      <div className="text-xs text-red-700 bg-red-50 p-2.5 rounded border border-red-100 font-medium">
                        ⚠ <strong>Leave Policy Has No Leave Types:</strong> The assigned leave policy does not contain any configured leave types.
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* From Date */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">From Date *</label>
                    <input
                      type="date"
                      required
                      value={adminLeaveForm.from_date}
                      onChange={(e) => setAdminLeaveForm({ ...adminLeaveForm, from_date: e.target.value })}
                      className={inputClass}
                    />
                  </div>

                  {/* To Date */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">To Date *</label>
                    <input
                      type="date"
                      required
                      value={adminLeaveForm.to_date}
                      onChange={(e) => setAdminLeaveForm({ ...adminLeaveForm, to_date: e.target.value })}
                      className={inputClass}
                    />
                  </div>

                  {/* Leave Type */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Leave Type *</label>
                    <select
                      required
                      disabled={Boolean(policyError) || policyCheckLoading}
                      value={adminLeaveForm.leave_type_id}
                      onChange={(e) => setAdminLeaveForm({ ...adminLeaveForm, leave_type_id: e.target.value })}
                      className={inputClass}
                    >
                      <option value="">-- Select Leave Type --</option>
                      {allowedLeaveTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name} ({type.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Number of Days (Calculated, Read-only) */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Calculated Days (Preview)</label>
                    <input
                      type="text"
                      readOnly
                      value={calculatedDays ? `${calculatedDays} Day(s)` : '0 Days'}
                      className={inputClass + ' bg-gray-100 text-gray-600 font-bold'}
                    />
                  </div>
                </div>

                {/* Leave Request Preview & Coverage Breakdown */}
                {previewLoading && (
                  <div className="text-xs text-blue-600 bg-blue-50 p-3 rounded border border-blue-100 flex items-center justify-center font-medium">
                    Calculating leave preview and balance conversion...
                  </div>
                )}

                {previewError && (
                  <div className="text-xs text-red-700 bg-red-50 p-3 rounded border border-red-100 font-medium">
                    ⚠ {previewError}
                  </div>
                )}

                {deductionPreview && (
                  <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 space-y-3">
                    <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider">Leave Request Summary & Coverage</h4>
                    <div className="grid grid-cols-2 gap-y-2 text-xs font-medium text-blue-800">
                      <div>Requested Days:</div>
                      <div className="font-bold text-right text-blue-950">{deductionPreview.requested_days} Day(s)</div>

                      <div>Available {deductionPreview.primary_leave?.leave_type} Balance:</div>
                      <div className="font-bold text-right text-blue-950">{deductionPreview.available_balance} Day(s)</div>

                      <div>Will be covered by {deductionPreview.primary_leave?.leave_type}:</div>
                      <div className="font-bold text-right text-blue-950">{deductionPreview.covered_days} Day(s)</div>

                      <div>Remaining Days:</div>
                      <div className="font-bold text-right text-blue-950">{deductionPreview.excess_days} Day(s)</div>
                    </div>

                    {deductionPreview.excess_days > 0 && (
                      <div className="pt-2.5 border-t border-blue-100/50 text-xs">
                        <div className="flex justify-between items-center bg-white p-2.5 rounded border border-blue-200">
                          <span className="font-semibold text-blue-800">Conversion / Treatment:</span>
                          <span className="font-bold text-blue-950">
                            {deductionPreview.conversion?.rule_exists
                              ? `${deductionPreview.conversion.rule_name} — ${deductionPreview.conversion.days} Day(s)`
                              : `Absence fallback (${deductionPreview.conversion?.target_type || 'LOP'}) — ${deductionPreview.excess_days} Day(s)`
                            }
                          </span>
                        </div>
                        {!deductionPreview.conversion?.rule_exists && (
                          <p className="text-[10px] text-blue-600 mt-1 font-medium">
                            * No conversion rule configured. The remaining days will be treated according to the existing Absence/unpaid fallback.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Medical Certificate */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Medical Certificate Submitted?</label>
                  <div className="flex items-center space-x-4">
                    <label className="inline-flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="medical_certificate"
                        checked={adminLeaveForm.medical_certificate === true}
                        onChange={() => setAdminLeaveForm({ ...adminLeaveForm, medical_certificate: true })}
                        className="text-blue-600 focus:ring-blue-500 h-4 w-4"
                      />
                      <span>Yes</span>
                    </label>
                    <label className="inline-flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="medical_certificate"
                        checked={adminLeaveForm.medical_certificate === false}
                        onChange={() => setAdminLeaveForm({ ...adminLeaveForm, medical_certificate: false })}
                        className="text-blue-600 focus:ring-blue-500 h-4 w-4"
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Reason / Remarks *</label>
                  <textarea
                    required
                    rows={4}
                    placeholder="Enter reason for leave (minimum 10 characters)..."
                    value={adminLeaveForm.reason}
                    onChange={(e) => setAdminLeaveForm({ ...adminLeaveForm, reason: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAdminCreateModal(false)}
                  className="px-4 py-2 rounded-lg border text-sm font-semibold hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || Boolean(policyError) || !adminLeaveForm.employee_id || !adminLeaveForm.leave_type_id}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Leave Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;
